/**
 * Feedback Readiness Process Handler
 * Checks if behavioral feedback/report is ready for display
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { State } from '../../hooks/useAppState'

export interface FeedbackReadinessResponse {
  isReady: boolean
  timeRemaining?: number // Time remaining in seconds (optional)
  error?: boolean // Indicates if feedback generation failed
}

// Track start time for mock implementation
let mockStartTime: number | null = null

/**
 * Check feedback readiness
 * Mock implementation: simulates feedback being ready after 1 minute
 */
export const checkFeedbackReadiness = async (_state: State): Promise<ApiResponse<FeedbackReadinessResponse>> => {
  return handleRequest(
    async () => {
      // Initialize start time on first call
      if (mockStartTime === null) {
        mockStartTime = Date.now()
      }

      const elapsed = Date.now() - mockStartTime
      const elapsedSeconds = Math.floor(elapsed / 1000)
      const timeRemaining = Math.max(0, 60 - elapsedSeconds)
      const isReady = elapsed >= 60000 // 1 minute = 60000ms

      return {
        data: {
          isReady,
          timeRemaining: isReady ? 0 : timeRemaining
        },
        success: true
      } as ApiResponse<FeedbackReadinessResponse>
    },
    async () => {
      // Real implementation: check feedback status via getLatestFeedbackDataForGamer
      try {
        const { getLatestFeedbackDataForGamer } = await import('./feedbackData')
        const { VoiceProcessingStatus } = await import('../../../../pure-js/types/Session')

        const feedbackResponse = await getLatestFeedbackDataForGamer(_state)

        if (!feedbackResponse.success || !feedbackResponse.data) {
          // No feedback data available yet, still processing
          return {
            data: {
              isReady: false,
              error: false
            },
            success: true
          } as ApiResponse<FeedbackReadinessResponse>
        }

        const feedbackData = feedbackResponse.data
        const voiceProcessingStatus = feedbackData.voiceProcessingStatus
        const feedbackReady = feedbackData.feedbackReady

        // Check if voiceProcessingStatus is null - treat as error
        if (voiceProcessingStatus === null) {
          return {
            data: {
              isReady: false,
              error: true
            },
            success: true
          } as ApiResponse<FeedbackReadinessResponse>
        }

        // Check if feedback generation failed
        if (
          voiceProcessingStatus === VoiceProcessingStatus.FAILED_MISSING_DATA ||
          voiceProcessingStatus === VoiceProcessingStatus.FAILED_ERROR
        ) {
          return {
            data: {
              isReady: false,
              error: true
            },
            success: true
          } as ApiResponse<FeedbackReadinessResponse>
        }

        // Check if feedback is completed - requires BOTH voice processing completed AND feedbackReady from backend
        if (voiceProcessingStatus === VoiceProcessingStatus.COMPLETED && feedbackReady === true) {
          return {
            data: {
              isReady: true,
              error: false
            },
            success: true
          } as ApiResponse<FeedbackReadinessResponse>
        }

        // Still in progress
        return {
          data: {
            isReady: false,
            error: false
          },
          success: true
        } as ApiResponse<FeedbackReadinessResponse>
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error checking feedback readiness:', error)
        // On error, assume still processing (don't show error yet)
        return {
          data: {
            isReady: false,
            error: false
          },
          success: true
        } as ApiResponse<FeedbackReadinessResponse>
      }
    }
  )
}

/**
 * Reset mock start time (useful for testing)
 */
export const resetFeedbackReadinessMock = (): void => {
  mockStartTime = null
}
